import React, { useState, useEffect, useMemo } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend,
} from 'recharts';
import { fetchLSTMAnalysis, fetchShotHistory } from '../services/api';
import AIModalShell from './AIModalShell';
import './AIProcessOptimizationModal.css';

/* 권장 항목 → 샷 이력 필드. 현재값은 선택한 샷의 실측값을 쓴다. */
const FEATURE_FIELD = {
  inj_speed_1: { field: 'injSpeed1', unit: 'mm/s' },
  temp_1_current: { field: 'temp1Current', unit: '°C' },
  inj_press_1: { field: 'injPress1', unit: 'bar' },
  charge_posi_1: { field: 'chargePosi1', unit: 'mm' },
  inj_end_posi: { field: 'injEndPosi', unit: 'mm' },
};

/* 권장값 대비 이 비율을 벗어나면 조정 대상으로 본다. */
const TOLERANCE = 0.015;

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
const fx = (v, d = 2) => (v === null || v === undefined ? '-' : Number(v).toFixed(d));

const RadarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="aipo-tip">
      <div className="aipo-tip-label">{label}</div>
      {payload.map((p) => (
        <div className="aipo-tip-row" key={p.dataKey}>
          <span className="aipo-tip-dot" style={{ background: p.color }} />
          <span>{p.name}</span>
          <strong>{Number(p.value).toFixed(1)}</strong>
        </div>
      ))}
      <div className="aipo-tip-foot">권장값을 100 으로 둔 상대값</div>
    </div>
  );
};

/**
 * AI 공정최적화 모달 — "어떻게 고치는가"에 답하는 처방 화면.
 * 좌측 Shot History 에서 고른 샷의 실측값이 '현재값' 이 된다.
 */
function AIProcessOptimizationModal({ open, onClose }) {
  const [lstm, setLstm] = useState(null);
  const [shots, setShots] = useState([]);
  const [selectedShot, setSelectedShot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setApplied(false);
    (async () => {
      setLoading(true);
      try {
        const [a, h] = await Promise.all([
          fetchLSTMAnalysis(null, null, null),
          fetchShotHistory(null, 100),
        ]);
        if (cancelled) return;
        setLstm(a?.data || null);
        const list = h?.data?.shots || [];
        setShots(list);
        setSelectedShot(list.length ? list[0].shotCount : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => { setApplied(false); }, [selectedShot]);

  const current = shots.find((s) => s.shotCount === selectedShot) || null;

  /* 권장값은 모델이 준 값, 현재값은 선택 샷의 실측값. 상태와 조정 방향은 둘의 차이로 정한다. */
  const rows = useMemo(() => (lstm?.recommendations || []).map((r) => {
    const m = FEATURE_FIELD[r.feature];
    const cur = m && current ? num(current[m.field]) : num(r.current_value);
    const rec = num(r.recommended_value);
    const diff = cur !== null && rec !== null ? cur - rec : null;
    const ratio = diff !== null && rec ? Math.abs(diff) / rec : 0;
    return {
      key: r.feature,
      name: r.feature_kr,
      unit: m?.unit || '',
      current: cur,
      recommended: rec,
      diff,
      ratio,
      direction: diff !== null && diff > 0 ? 'decrease' : 'increase',
      alert: ratio > TOLERANCE,
    };
  }), [lstm, current]);

  const outOfRange = rows.filter((r) => r.alert);
  /* 개선 여지는 '조정이 필요한 항목'의 이탈 폭만으로 잡는다. 허용 범위 안의 흔들림까지
     더하면 고칠 게 없는데도 개선율이 나와 앞뒤가 안 맞는다. */
  const deviation = outOfRange.reduce((s, r) => s + r.ratio, 0);

  /* 예상 개선 효과. 실제 모델이 붙으면 이 계산을 API 응답으로 대체한다. */
  const beforeRate = Math.round((2.4 + deviation * 18) * 10) / 10;
  const afterRate = Math.round(Math.max(0.3, beforeRate - deviation * 15) * 10) / 10;
  const cutRate = beforeRate > 0 ? Math.round(((beforeRate - afterRate) / beforeRate) * 1000) / 10 : 0;

  /* 권장값을 100 으로 두고 현재값을 상대화한다. 항목마다 단위가 달라 그대로는 못 겹친다. */
  const radar = rows.map((r) => ({
    subject: r.name,
    current: r.recommended ? Math.round((r.current / r.recommended) * 1000) / 10 : 0,
    recommended: 100,
  }));

  return (
    <AIModalShell open={open} onClose={onClose} icon="tune" title="AI 공정최적화">
      {loading || !lstm ? (
        <div className="aim-empty">최적화 결과를 불러오는 중...</div>
      ) : (
        <div className="aipo-grid">
          {/* ── 좌측: Shot History ── */}
          <section className="aim-pane aipo-shots">
            <h3 className="aim-title">
              Shot History
              <span className="aim-title-note">선택한 샷이 현재값</span>
            </h3>
            <div className="aipo-shot-scroll">
              <table className="aipo-shot-table">
                <thead>
                  <tr>
                    <th>샷</th><th>사출속도</th><th>용융온도</th><th>충진압력</th><th>판정</th>
                  </tr>
                </thead>
                <tbody>
                  {shots.map((s) => (
                    <tr
                      key={s.shotCount}
                      className={s.shotCount === selectedShot ? 'selected' : undefined}
                      onClick={() => setSelectedShot(s.shotCount)}
                    >
                      <td className="aipo-shot-num">#{s.shotCount}</td>
                      <td>{fx(s.injSpeed1, 1)}</td>
                      <td>{fx(s.temp1Current, 1)}</td>
                      <td>{fx(s.injPress1, 1)}</td>
                      <td>
                        <span className={'aim-badge ' + (s.qualityStatus === 'GOOD' ? 'good' : 'bad')}>
                          {s.qualityStatus === 'GOOD' ? '양' : '불'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── 가운데 위: 공정 튜닝 추천값 ── */}
          <section className="aim-pane">
            <h3 className="aim-title">
              공정 튜닝 추천값
              <span className="aim-title-note">
                {current ? '#' + current.shotCount + ' 기준' : '-'} · 허용 &plusmn;{TOLERANCE * 100}%
              </span>
            </h3>
            <div className="aipo-table-wrap">
              <table className="aim-table aipo-table">
                <thead>
                  <tr>
                    <th>항목</th><th>현재값</th><th>권장값</th>
                    <th>차이</th><th>조정</th><th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key} className={r.alert ? 'aim-row-alert' : undefined}>
                      <td className="aim-td-name">{r.name}</td>
                      <td>{fx(r.current)}<span className="aipo-unit">{r.unit}</span></td>
                      <td className="aipo-rec">{fx(r.recommended)}<span className="aipo-unit">{r.unit}</span></td>
                      <td style={{ color: r.alert ? '#EF4444' : '#8B949E' }}>
                        {r.diff === null ? '-' : (r.diff > 0 ? '+' : '') + fx(r.diff)}
                      </td>
                      <td>
                        <span className={'aipo-dir ' + r.direction}>
                          {r.direction === 'decrease' ? '↓ 낮춤' : '↑ 높임'}
                        </span>
                      </td>
                      <td>
                        <span className={'aim-badge ' + (r.alert ? 'bad' : 'good')}>
                          {r.alert ? '범위 이탈' : '정상'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── 가운데 아래: 최적화 요약 ── */}
          <section className="aim-pane">
            <h3 className="aim-title">최적화 요약</h3>
            <div className="aipo-stat-row">
              <div className="aipo-stat">
                <span>조정 필요</span>
                <strong style={{ color: outOfRange.length ? '#EF4444' : '#22C55E' }}>
                  {outOfRange.length}<em>/{rows.length}</em>
                </strong>
              </div>
              <div className="aipo-stat">
                <span>예상 불량률</span>
                <strong style={{ color: '#22C55E' }}>{afterRate}<em>%</em></strong>
              </div>
              <div className="aipo-stat">
                <span>개선 폭</span>
                <strong style={{ color: '#22D3EE' }}>{cutRate}<em>%</em></strong>
              </div>
            </div>

            <div className="aipo-effect">
              <div className="aipo-effect-bar">
                <span className="aipo-effect-label">현재</span>
                <span className="aipo-effect-track">
                  <span className="aipo-effect-fill"
                        style={{ width: Math.min(100, beforeRate * 8) + '%', background: '#EF4444' }} />
                </span>
                <span className="aipo-effect-value">{beforeRate}%</span>
              </div>
              <div className="aipo-effect-bar">
                <span className="aipo-effect-label">적용 후</span>
                <span className="aipo-effect-track">
                  <span className="aipo-effect-fill"
                        style={{ width: Math.min(100, afterRate * 8) + '%', background: '#22C55E' }} />
                </span>
                <span className="aipo-effect-value">{afterRate}%</span>
              </div>
            </div>

            <button
              type="button"
              className={'aipo-apply' + (applied ? ' done' : '')}
              onClick={() => setApplied(true)}
              disabled={applied}
            >
              <span className="material-symbols-outlined">{applied ? 'check_circle' : 'send'}</span>
              {applied ? '설비에 전송됨' : '권장값 설비에 전송'}
            </button>
            <p className="aipo-note">데모 화면입니다. 실제 설비로 값이 전송되지는 않습니다.</p>
          </section>

          {/* ── 우측: 현재값 vs 권장값 ── */}
          <section className="aim-pane aipo-radar-pane">
            <h3 className="aim-title">
              현재값 vs 권장값
              <span className="aim-title-note">권장값 = 100</span>
            </h3>
            <div className="aim-chart">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radar} margin={{ top: 20, right: 34, bottom: 10, left: 34 }}>
                  <PolarGrid stroke="#232A36" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#C9D1D9' }} />
                  <PolarRadiusAxis domain={[60, 140]} tickCount={5}
                                   tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} />
                  <Radar name="권장값" dataKey="recommended" stroke="#8B949E"
                         strokeDasharray="4 3" strokeWidth={1.2} fill="none" isAnimationActive={false} />
                  <Radar name="현재값" dataKey="current" stroke="#22D3EE" strokeWidth={1.8}
                         fill="#22D3EE" fillOpacity={0.10} isAnimationActive={false}
                         dot={{ r: 2.5, fill: '#22D3EE', stroke: '#0D1117', strokeWidth: 1 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Tooltip content={<RadarTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="aipo-radar-legend">
              {rows.map((r) => (
                <div className={'aipo-radar-item' + (r.alert ? ' alert' : '')} key={r.key}>
                  <span>{r.name}</span>
                  <strong>
                    {r.recommended ? ((r.current / r.recommended) * 100).toFixed(1) : '-'}
                  </strong>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </AIModalShell>
  );
}

export default AIProcessOptimizationModal;
